/**
 * localStorage persistence for the login disclaimer flow.
 *
 * Three key families, all per-user (`{sub}` is the Auth0 subject, or `anon`
 * before the identity snapshot resolves):
 * - `disclaimer.ack.{sub}` — acknowledgement record `{ts, hash}`. Valid only
 *   while inside the 24h window AND the hash matches the content currently
 *   shown, so changing the disclaimer text forces an immediate re-ack. Legacy
 *   values (plain timestamp, or the marker '1') predate custom texts and are
 *   treated as acks of the default text.
 * - `disclaimer.required.{sub}` — cached server answer to "must this user see
 *   the disclaimer", so offline launches don't flash it for opted-out users.
 * - `disclaimer.texts.{sub}` — cached custom disclaimer texts (JSON array),
 *   so offline launches show the same content the server last sent.
 */

const DISCLAIMER_ACK_STORAGE_PREFIX = 'disclaimer.ack.'
const DISCLAIMER_REQUIRED_STORAGE_PREFIX = 'disclaimer.required.'
const DISCLAIMER_TEXTS_STORAGE_PREFIX = 'disclaimer.texts.'
export const DISCLAIMER_WINDOW_MS = 24 * 60 * 60 * 1000

export type DisclaimerRequirement = 'required' | 'not-required' | 'unknown'

export interface DisclaimerAckRecord {
  ts: number
  hash: string
}

export function getDisclaimerStorageKey(userSub: string | null): string {
  return `${DISCLAIMER_ACK_STORAGE_PREFIX}${userSub ?? 'anon'}`
}

export function getDisclaimerRequirementKey(userSub: string | null): string {
  return `${DISCLAIMER_REQUIRED_STORAGE_PREFIX}${userSub ?? 'anon'}`
}

export function getDisclaimerTextsKey(userSub: string | null): string {
  return `${DISCLAIMER_TEXTS_STORAGE_PREFIX}${userSub ?? 'anon'}`
}

/**
 * FNV-1a 32-bit hash (hex) of the disclaimer content the user is shown.
 * Texts are NUL-joined so boundaries can't collide (["ab","c"] ≠ ["a","bc"]).
 */
export function hashDisclaimerTexts(texts: string[]): string {
  const joined = texts.join('\u0000')
  let hash = 0x811c9dc5
  for (let i = 0; i < joined.length; i += 1) {
    hash ^= joined.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16)
}

export function parseAckRecord(
  raw: string | null,
  now: number,
  defaultHash: string,
): DisclaimerAckRecord | null {
  if (!raw) {
    return null
  }
  if (raw === '1') {
    // Oldest legacy marker: ack'd "recently", against the default text.
    return { ts: now, hash: defaultHash }
  }
  const numeric = Number(raw)
  if (Number.isFinite(numeric)) {
    // Legacy plain timestamp: ack'd before custom texts existed, so it was
    // necessarily against the default text.
    return { ts: numeric, hash: defaultHash }
  }
  try {
    const parsed = JSON.parse(raw) as { ts?: unknown; hash?: unknown }
    if (
      parsed &&
      typeof parsed === 'object' &&
      typeof parsed.ts === 'number' &&
      Number.isFinite(parsed.ts) &&
      typeof parsed.hash === 'string'
    ) {
      return { ts: parsed.ts, hash: parsed.hash }
    }
  } catch {
    // fall through to null
  }
  return null
}

function isWithinWindow(timestamp: number, now: number): boolean {
  return now - timestamp < DISCLAIMER_WINDOW_MS
}

/**
 * Whether a valid acknowledgement exists for the given content hash. Expired
 * or hash-mismatched entries are dropped; an ack stored under the anon key is
 * migrated to the user's key once the sub is known (matching the pre-existing
 * fallback behaviour). Legacy-format entries are rewritten as JSON.
 */
export function readAcknowledged(
  key: string,
  userSub: string | null,
  currentHash: string,
  defaultHash: string,
): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  try {
    const now = Date.now()
    const direct = parseAckRecord(
      window.localStorage.getItem(key),
      now,
      defaultHash,
    )
    if (direct && isWithinWindow(direct.ts, now) && direct.hash === currentHash) {
      window.localStorage.setItem(key, JSON.stringify(direct))
      return true
    }
    if (direct) {
      window.localStorage.removeItem(key)
    }
    const fallbackKey = getDisclaimerStorageKey(null)
    const fallback = parseAckRecord(
      window.localStorage.getItem(fallbackKey),
      now,
      defaultHash,
    )
    if (
      fallback &&
      isWithinWindow(fallback.ts, now) &&
      fallback.hash === currentHash
    ) {
      window.localStorage.setItem(fallbackKey, JSON.stringify(fallback))
      if (userSub) {
        window.localStorage.setItem(key, JSON.stringify(fallback))
      }
      return true
    }
    if (fallback) {
      window.localStorage.removeItem(fallbackKey)
    }
    return false
  } catch {
    return false
  }
}

export function persistAcknowledged(key: string, hash: string): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    const record: DisclaimerAckRecord = { ts: Date.now(), hash }
    window.localStorage.setItem(key, JSON.stringify(record))
  } catch {
    // ignore storage failures (e.g., private mode)
  }
}

export function readCachedDisclaimerRequirement(
  key: string,
): DisclaimerRequirement {
  if (typeof window === 'undefined') {
    return 'unknown'
  }
  try {
    const raw = window.localStorage.getItem(key)
    if (raw === 'false') {
      return 'not-required'
    }
    if (raw === 'true') {
      return 'required'
    }
    return 'unknown'
  } catch {
    return 'unknown'
  }
}

export function persistDisclaimerRequirement(
  key: string,
  required: boolean,
): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(key, required ? 'true' : 'false')
  } catch {
    // ignore storage failures (e.g., private mode)
  }
}

export function readCachedTexts(key: string): string[] {
  if (typeof window === 'undefined') {
    return []
  }
  try {
    const raw = window.localStorage.getItem(key)
    if (!raw) {
      return []
    }
    const parsed: unknown = JSON.parse(raw)
    if (!Array.isArray(parsed)) {
      return []
    }
    return parsed.filter((text): text is string => typeof text === 'string')
  } catch {
    return []
  }
}

export function persistCachedTexts(key: string, texts: string[]): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    window.localStorage.setItem(key, JSON.stringify(texts))
  } catch {
    // ignore storage failures (e.g., private mode)
  }
}

function clearByPrefix(prefix: string): void {
  if (typeof window === 'undefined') {
    return
  }
  try {
    const keys = Object.keys(window.localStorage).filter((key) =>
      key.startsWith(prefix),
    )
    for (const key of keys) {
      window.localStorage.removeItem(key)
    }
  } catch {
    // ignore storage failures
  }
}

export function clearAllAcknowledged(): void {
  clearByPrefix(DISCLAIMER_ACK_STORAGE_PREFIX)
}

export function clearAllRequirementCache(): void {
  clearByPrefix(DISCLAIMER_REQUIRED_STORAGE_PREFIX)
}

export function clearAllCachedTexts(): void {
  clearByPrefix(DISCLAIMER_TEXTS_STORAGE_PREFIX)
}
